import React, { useContext, useEffect, useState, useRef } from 'react';
import pathObj from '../../services/Config';
import { checkImage, decryptData, errorHandling, UpdatedDecimalDigits, unavailableItemsMessage } from '../../helpers/common';
import { CommonService } from '../../services/common';
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import { CartContext } from "../../context/cartContext";
import RecentpurchaseItem from './RecentpurchaseItem';
import toast from 'react-hot-toast';
import debounce from 'lodash.debounce';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useCallback } from 'react';

const Cart = () => {
  const userInfo = window.localStorage.getItem('userData');
  const userD = decryptData(userInfo);
  const compData = JSON.parse(userD);
  const currencyName = compData?.Currency
  const currencySymbol = getSymbolFromCurrency(currencyName);
  const customerId = compData?.customer_id;
  const [cartDetails, setCartDetails] = useState([]);
  const [errorMessage, setErrorMessage] = useState();
  const [vatData, setVatData] = useState('');
  const category = localStorage.getItem('categoriesName');
  const [quantities, setQuantities] = useState({});
  const [imageStatus, setImageStatus] = useState({});
  const [priceBreakDown, setPriceBreakDown] = useState('');
  const [priceDropdownVisible, setPriceDropdownVisible] = useState({});
  const { removeFromCart } = useContext(CartContext);
  const [wishlistStatus, setWishlistStatus] = useState({});
  const Currency = compData?.Currency;
  const PriceListNum = compData?.PriceListNum;
  const { setCountCart } = useContext(CartContext);
  const [isCartEmpty, setIsCartEmpty] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedPriceBreakdown, setSelectedPriceBreakdown] = useState({});

  // an item withdrawn from sale still sits in older baskets; SAP rejects the whole
  // order if one line is no longer sold, so it has to be removed before checkout
  const isDiscontinued = (item) =>
    item?.U_V33_WebShowOnline != null && item?.U_V33_WebShowOnline !== 'Y';

  const handleCheckout = () => {
    const unavailable = cartDetails?.filter(isDiscontinued) || [];
    if (unavailable.length > 0) {
      toast.error(unavailableItemsMessage(unavailable.map(it => it?.ItemCode)));
      return;
    }

    const isEmpty = cartDetails?.length === 0 || cartDetails?.every(item => (quantities[item?.ItemCode] || item?.quantity) === 0);
    // let is_productsValid = false;

    // cartDetails.forEach(prod => {
    //   if (prod?.IsUsedInCart == 1) {
    //     is_productsValid = true;
    //   }
    // });
    // console.log("is_productsValid", is_productsValid)
    // return is_productsValid;
    
    if (isEmpty && (errorMessage == false || errorMessage != 'undefined')) {      
      setIsCartEmpty(true);
      if(isEmpty && errorMessage == false){
        toast.error('Your cart is empty. Please add products to your cart before proceeding to checkout.')
      }      
    } else {
      navigate('/checkout');
    }
  };

  const fetchCartData = debounce(async () => {
    setLoading(true);
    try {
      const customer_id = customerId;
      const urlCart = `${pathObj.baseURL}${pathObj.getCartDetails}`;
      const payloadCart = { customer_id, category, Currency, PriceListNum };

      // Fetch cart details
      const cartData = await CommonService.getAllWithBodyWithToken(urlCart, payloadCart);
      const products = cartData?.data || [];
      const initialQuantities = {};
      
      products.forEach((product) => {
        initialQuantities[product.ItemCode] = product.quantity || 1;
      });
      setQuantities(initialQuantities);
      setVatData(cartData?.vat)
      setCartDetails(products);
      setCountCart(cartData?.count)
      // Set quantities with ItemCode as key
      

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrorMessage(error?.response?.data?.success)
      console.error("Error fetching page data:", error);
      errorHandling(error);
    }
  }, 300);

  useEffect(() => {
    fetchCartData();
  }, [customerId]);

  const loadImages = async () => {
    const status = { ...imageStatus }; // Copy the current image status
    for (const product of cartDetails) {
      const pictureCheck = await checkImage(product?.Picture);
      const vectorPictureCheck = await checkImage(product?.vectorPicture);

      // Check if the picture URL works, if not check vectorPicture, if both fail use the no-image.jpg
      if (pictureCheck?.status === "ok") {
        status[product?.ItemCode] = pictureCheck?.path;
      } else if (vectorPictureCheck?.status === "ok") {
        status[product?.ItemCode] = vectorPictureCheck?.path;
      } else {
        status[product?.ItemCode] = `${pathObj?.s3ImagePath}/no-image.jpg`;
      }

      // Update image status for the current product as soon as it's ready
      setImageStatus((prevStatus) => ({ ...prevStatus, [product?.ItemCode]: status[product?.ItemCode] }));
    }
  };

  const debouncedFetchLoadImages = debounce(loadImages, 300); // Adjust the delay as needed
  useEffect(() => {
    if (cartDetails?.length > 0) {
      debouncedFetchLoadImages();
    }
  }, [cartDetails]);

  useEffect(() => {
    if (cartDetails?.length > 0) {
      const itemIds = cartDetails?.map(item => item?.id).join(',');
      const ItemCodes = cartDetails?.map(item => item?.ItemCode).join(',');
      localStorage.setItem('checkoutItemIds', itemIds);
      localStorage.setItem('checkoutItemCodes', ItemCodes);
    }
  }, [cartDetails]);

  const handleRemoveItem = async (itemId, productId) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel!',
    });

    if (confirm.isConfirmed) {
      try {
        const url = `${pathObj.baseURL}${pathObj.deleteIndividualItem}?id=${itemId}`;
        await CommonService.delete(url);
        setCartDetails(prev => prev.filter(item => item.cart_item_id !== itemId));
        removeFromCart(productId);
        // Swal.fire('Deleted!', 'Your item has been deleted.', 'success');
      } catch (error) {
        console.error("Error deleting item:", error);
        Swal.fire('Error!', 'There was an error deleting the item.', 'error');
      }
    }
  };

  const togglePriceDropdown = (itemCode) => {
    if (!priceDropdownVisible[itemCode]) {
      setPriceDropdownVisible((prevVisible) => ({
        ...prevVisible,
        [itemCode]: !prevVisible[itemCode],
      }));
    }
  };

  const handlePriceBreakdownClick = (itemCode, quantity, priceBreakDownId, price) => {
    setPriceBreakDown(priceBreakDownId);
    // setSelectedPriceBreakdown((prevBreakdowns) => ({
    //   ...prevBreakdowns,
    //   [itemCode]: {
    //     quantity,
    //     price, // Use the passed price
    //     priceBreakDownId,
    //   },
    // }));
    const item = cartDetails.find(it => it.ItemCode === itemCode);
    handleQuantityChange(item, null, quantity, priceBreakDownId, price);
  };

  const inputRefs = useRef({});

  useEffect(() => {
  const lastFocused = localStorage.getItem('lastFocusedItemCode');
    if (
      lastFocused &&
      inputRefs.current[lastFocused] &&
      cartDetails.some(item => item.ItemCode === lastFocused)
    ) {
      inputRefs.current[lastFocused].focus();
    }
  }, [cartDetails]); // <-- depend on cartDetails

  const debouncedUpdateCart = useCallback(
    debounce(async (payload) => {
      try {
        const response = await CommonService.getAllWithBodyWithToken(`${pathObj.baseURL}${pathObj.addCart}`, payload);
        if (response.success) {
          toast.success('Quantity updated successfully');
          fetchCartData();
        } else {
          toast.error('Failed to update quantity');
        }
      } catch (error) {
        console.error("Error updating quantity:", error);
      }
    }, 2000), [] // 2 seconds debounce
  );
  
  const handleQuantityChange = async (item, type, customQuantity = null, customPriceBreakdownId = null, breakDownPrice=null) => {
    const itemCode = item?.ItemCode;
    localStorage.setItem('lastFocusedItemCode', itemCode);

    let updatedQuantities;
    let updatedPriceBreakdownId = customPriceBreakdownId || priceBreakDown;

    await setQuantities((prevQuantities) => {
      const newQuantities = { ...prevQuantities };
      let currentQuantity = newQuantities[itemCode] || cartDetails?.find(it => it?.ItemCode === itemCode)?.quantity || 1;
      currentQuantity = parseInt(currentQuantity, 10);

      if (customQuantity !== null) {
        currentQuantity = parseInt(customQuantity, 10);
      } else if (type === "plus") {
        currentQuantity += 1;
      } else if (type === "minus" && currentQuantity > 1) {
        currentQuantity -= 1;
      }

      if (isNaN(currentQuantity)) {
        toast.error('Quantity required, minimum is 1');
        currentQuantity = 0;
      }

      newQuantities[itemCode] = currentQuantity;
      updatedQuantities = newQuantities;

      setPriceDropdownVisible((prevVisible) => ({
        ...prevVisible,
        [itemCode]: currentQuantity > 1,
      }));

    return newQuantities;
    });  

    const product = cartDetails.find(it => it.ItemCode === itemCode);
    if (product) {
      const quantity = updatedQuantities[itemCode] || 1;
      const applicableBreakdowns = product.PricesBreakdown.filter(breakdown => quantity >= breakdown.Quantity);
      const priceBreakdown = applicableBreakdowns[applicableBreakdowns.length - 1] || {};

      const priceBDownIdToUse = priceBreakdown?.id || '';
      const priceToUse = priceBreakdown?.SpecialPrice || product?.Price;
      const product_price_id = product?.product_price_id || '';

      const payload = {
        customer_id: customerId,
        product_id: product?.id,
        quantity,
        price_break_down_id: priceBDownIdToUse,
        product_price_id,
        action: 'updateCartQuantity',
        Csprice: breakDownPrice ? breakDownPrice : product?.Csprice > 0 && !priceToUse ? product?.Csprice : priceToUse,
        price: breakDownPrice ? breakDownPrice : product?.Csprice > 0 && !priceToUse ? product?.Csprice : priceToUse,
        product_itemcode: product?.ItemCode,
        customer_cardcode: compData?.CardCode,
      };

      // Debounce only if type is "input"
      if (type === "input") {
        debouncedUpdateCart(payload);
      } else {
        await CommonService.getAllWithBodyWithToken(`${pathObj.baseURL}${pathObj.addCart}`, payload).then(response => {
          if (response.success) {
            toast.success('Quantity updated successfully');
            fetchCartData();
          } else {
            toast.error('Failed to update quantity');
          }
        });
      }

      setPriceBreakDown(priceBDownIdToUse);
    }
  };

  const handleProductDetailsClick = (product) => {
    localStorage.setItem('productType', product?.ProductType);
    const formattedItemCode = product?.ItemCode.replace(/\//g, '__'); // Replace slashes with hyphens
    navigate(`/products/${formattedItemCode}`);
  };

  const handleProductGroupDetailsClick = (product) => {
    localStorage.setItem('productType', product?.ProductType);
    const formattedItemCode = product?.ItemCode.replace(/\//g, '__'); // Replace slashes with hyphens
    navigate(`/group-products/${formattedItemCode}`);
  }

  const handleWishlistToggle = async (productItemCode) => {
    const currentStatus = wishlistStatus[productItemCode] || false; // Get current status of the product
    const url = `${pathObj.baseURL}/saveFavoriteItem`; // Update with your API endpoint
    const payload = {
      customer_id: customerId,
      product_id: productItemCode,
      status: currentStatus ? 0 : 1, // Toggle wishlist status
    };

    try {
      const response = await CommonService.getAllWithBodyWithToken(url, payload);
      if (response.success) {
        // Update the wishlist status for the specific product
        setWishlistStatus((prevStatus) => ({
          ...prevStatus,
          [productItemCode]: !currentStatus, // Toggle the wishlist status
        }));
        toast.success(response?.message || "Wishlist updated successfully!");
        setTimeout(() => {
          fetchCartData();
        }, 300);
      } else {
        toast.error(response.message || "Failed to update wishlist.");
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
      toast.error("An error occurred while updating the wishlist.");
      errorHandling(error);
    }
  };

  const subtotal = cartDetails.reduce((acc, item) => {
    if (item?.IsUsedInCart == 0) {
      return acc + ((item?.Csprice || item?.price) * (quantities[item?.ItemCode] || item?.quantity));
    }
    return acc;
  }, 0);  
  const vat = subtotal * vatData / 100; // Calculate 20% of subtotal
  const total = subtotal + vat; // Total is subtotal plus VAT

  return (
    <>
      <div className="checkout_wrap">
        <div className="container">
          <div className="clinner">
            <h1>Shopping Cart</h1>
            <div className="d-flex flex-wrap">
              <div className="cart_left" style={{ position: 'relative' }}>
                <div className="cart_tbl">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="product_thumbnail">Product</th>
                        <th className="product_name">&nbsp;</th>
                        <th className="product_description">Description</th>
                        <th className="product_price">Price</th>
                        <th className="product_quantity text-center">Quantity</th>
                        <th className="product_subtotal text-end">Subtotal</th>
                        <th className="product_remove text-end">&nbsp;</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
                        <div className="loader cart_loader"></div>
                      ) : (
                        <>
                          {cartDetails.length > 0 ? (
                            cartDetails.map((item, index) => {
                              return (
                                <>
                                <tr key={index} className={(item?.IsUsedInCart == 1 || isDiscontinued(item)) ? 'invalid-product' : ''}>
                                  <td className="product_thumbnail">
                                    {/* {!imageStatus[item?.ItemCode] ? (
                                    <div className="loader"></div> // Show loader until image status is resolved
                                  ) : ( */}
                                    <a href="#">
                                      <img
                                        src={imageStatus[item?.ItemCode] || item?.Picture || item?.vectorPicture || `${pathObj?.s3ImagePath}/no-image.jpg`}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = `${pathObj?.s3ImagePath}/no-image.jpg`;
                                        }}
                                        alt={item.ItemName}
                                         />
                                    </a>
                                    {/* )} */}
                                  </td>
                                  <td className="product_name">
                                    <div className="favorite_icon">
                                      <img
                                        src={`${pathObj?.s3ImagePath}/wishlist-icon${wishlistStatus[item?.id] ? '-red' : ''}.svg`} // Check status from wishlistStatus state
                                        alt="Wishlist Icon"
                                        onClick={() => handleWishlistToggle(item?.id)}
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </div>
                                    <p>{item.ItemCode}</p>
                                    <p>{item.ItemName}</p>
                                    {item.AttributesList && Object.keys(item?.AttributesList).map(attrKey => (
                                      <div className="variant" key={attrKey}>
                                        <span>{item?.AttributesList[attrKey].filter_label}:</span> {item?.AttributesList[attrKey].filter_label_data.join(', ')}
                                      </div>
                                    ))}                                    
                                  </td>
                                  <td className="product_description" data-title="Description">
                                    {item.U_V33_WebDescription || item?.ItemName}
                                  </td>
                                  <td className="product_price" data-title="Price">{currencySymbol}{item?.Csprice > 0 ? item?.Csprice : item?.MinProductPrice ? item?.MinProductPrice : item?.price}</td>
                                  <td className="product_quantity text-center" data-title="Quantity">
                                    <div className={`cart_qty ${item?.IsUsedInCart == 1 ? 'disable_cursor' : ''}`}>
                                      <div className="qty_plusminus" 
                                        onClick={() => togglePriceDropdown(item?.ItemCode)}
                                        style={{ cursor: item?.IsUsedInCart == 1 ? 'no-drop' : 'pointer' }}>
                                        <button
                                          type="button"
                                          data-type="minus"
                                          onClick={() => handleQuantityChange(item, "minus")}
                                          disabled={item.quantity <= 1}
                                        >
                                          <img
                                            src={`${pathObj?.s3ImagePath}/minus-icon.svg`}
                                            style={{ opacity: item.quantity <= 1 ? 0.5 : 1 }}
                                            alt=""
                                          />
                                        </button>
                                        <input
                                          type="text"
                                          style={{ width: '40px' }}
                                          value={quantities[item?.ItemCode] && !isNaN(quantities[item?.ItemCode]) ? quantities[item?.ItemCode] : '' }
                                          onChange={(e) => handleQuantityChange(item, "input", e.target.value)}
                                          ref={(el) => (inputRefs.current[item.ItemCode] = el)}
                                          onFocus={(e) => {
                                            // Select the entire value so typing overwrites it
                                            try { e.target.select(); } catch (err) { /* ignore */ }
                                            localStorage.setItem('lastFocusedItemCode', item.ItemCode);
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === '-' || e.key === 'e') {
                                              e.preventDefault();
                                            }
                                          }}
                                        />
                                        <button
                                          type="button"
                                          data-type="plus"
                                          onClick={() => handleQuantityChange(item, "plus")}
                                        >
                                          <img
                                            src={`${pathObj?.s3ImagePath}/plus-icon.svg`}
                                            alt=""
                                          />
                                        </button>
                                      </div>
                                    </div>
                                    {item?.IsUsedInCart == 0 && 
                                      <div className="price_dropdow"
                                        style={{
                                          display:
                                            priceDropdownVisible[item?.ItemCode] &&
                                              item?.PricesBreakdown?.length > 0
                                              ? "block"
                                              : "none",
                                        }}
                                      >
                                        <div className="more_qty">
                                          {item?.PricesBreakdown?.map((priceBreakdown) => {
                                            return (
                                              <div
                                                className="more_qty_row"
                                                key={priceBreakdown.id}
                                                onClick={() => handlePriceBreakdownClick(item?.ItemCode, priceBreakdown?.Quantity, priceBreakdown?.id, priceBreakdown?.SpecialPrice)}
                                                style={{ cursor: "pointer" }}
                                              >
                                                <label>
                                                  {priceBreakdown?.Quantity}qty
                                                </label>
                                                <span>
                                                  {currencySymbol}{UpdatedDecimalDigits(priceBreakdown?.SpecialPrice)}
                                                </span>
                                              </div>
                                            )
                                          })}
                                        </div>
                                      </div>
                                    }
                                  </td>
                                  <td className="product_subtotal text-end" data-title="Subtotal">
                                    {/* {currencySymbol}{(item.Price * quantities[item?.ItemCode] || item.quantity).toFixed(2)} */}
                                    {currencySymbol}{UpdatedDecimalDigits(item.total)}
                                  </td>
                                  <td className="product_remove text-end" data-title="Remove">
                                    <a href="#" onClick={() => handleRemoveItem(item.cart_item_id, item.id)}>
                                      <img src={`${pathObj?.s3ImagePath}/close-icon2.svg`} alt="remove" />
                                    </a>
                                  </td>
                                </tr>
                                {item?.IsUsedInCart == 1 && 
                                  <tr className='invalid-product'>
                                    <td colSpan={7} style={{ padding: '0 20px', borderBottom: '1px solid #FFE9BC' }}>
                                      <p className='invalid-text'><b>Note: </b>The price for this product has changed. Please remove it from your cart and add it again to proceed.</p> 
                                    </td>
                                  </tr>
                                }
                                {isDiscontinued(item) &&
                                  <tr className='invalid-product'>
                                    <td colSpan={7} style={{ padding: '0 20px', borderBottom: '1px solid #FFE9BC' }}>
                                      <p className='invalid-text'><b>Note: </b>{unavailableItemsMessage([item?.ItemCode])}</p>
                                    </td>
                                  </tr>
                                }
                                </>
                              )
                            })
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center">Your cart is empty</td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="cart_summary">
                <div className="summary_bx">
                  <h3>Summary</h3>
                  <ul>
                    <li>
                      <label>Subtotal:</label>
                      <span>{currencySymbol}{UpdatedDecimalDigits(subtotal)}</span>
                    </li>
                    <li>
                      <label>VAT({vatData})%:</label>
                      <span>{currencySymbol}{UpdatedDecimalDigits(vat)}</span>
                    </li>
                    <li>
                      <label>Total:</label>
                      <span>{currencySymbol}{UpdatedDecimalDigits(total)}</span>
                    </li>
                  </ul>
                  <div className="btn_row pb-1">
                    <button
                      onClick={handleCheckout}
                      className='button2'
                    >
                      Proceed To Checkout
                    </button>
                  </div>
                </div>
              </div>
              <RecentpurchaseItem fetchCartData={fetchCartData} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Cart;
